/* eslint-disable */
import React, { useState } from "react";
import { Button} from "@hdir/ui/button";
import { TextField } from "@hdir/ui/text-field";
import { evaluate } from "mathjs";
import ThemeBox from "/react4xp/shared/ThemeBox";
import Richtext from "/react4xp/shared/Richtext";
import { listify } from "/lib/util/type-helpers";

import type Calc from "../types.d";

/*
Description:
Calculates bmi.

Required inputs:
- Need inputs for atleast height and weight. Names can be anything.

Required constants
- none

Required calculator:
- One calculator is used. This is the formula used to calculate BMI.

Required for Results:
- Need one "Calculations and static values".
- The richtext field here will always be displayed at the bottom of the result box.
  - Need one "Static values table" for the results.
  - first and second colums can be empty.
  - Each entry is a different result to display based on the calculated BMI.
    - The value of each entry is used to decide what resusult to show. This works by showing the highest value result, that is lower then the calculated BMI.

Required files:
- none

Things to know:
The inputs and formua can be anything, so it is easy to make the calculation more complex.

*/

type Props = {
  data: Record<string, any>,
  inputs: Calc.UserInput,
  formula: Calc.Formula,
  staticInputs: Calc.CalcStaticInputs
}
function Part(props: Props) {
  const inputsStateObj = props.inputs.reduce((valuesObject, { techName, value }) => ({ ...valuesObject, [techName]: value || 0 }), {});

  const [values, setValues] = useState(inputsStateObj);
  const [bmi, setBmi] = useState({
    value: 0,
    data: null
  });

  const updateValue = (key: string, value: number) => {
    setValues({
      ...values,
      [key]: value
    });
  };

  const getInputHandler = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!(key in values)) return;
    updateValue(key, parseFloat(e.target.value as string));
  };

  const updateBmi = (event: React.MouseEvent) => {
    event.preventDefault();

    /** see docs for valid syntax: https://mathjs.org/docs/expressions/syntax.html */
    const bmi = evaluate(listify(props.formula)[0].formula, values);
    // round to one decimal places
    const bmiRounded = Math.round(bmi * 10) / 10;

    const staticInput = props.staticInputs[0];
    const staticValTable = listify(staticInput.staticValuesTable)[0];

    setBmi({
      value: bmiRounded,
      data: getResultData(bmiRounded, listify(staticValTable.staticValues))
    });
  };

  /** return the element of static values where the elements value is the highest, but lower than bmi. This lets us add ranges, and not worry about the list being sorted. */
  const getResultData = (bmi: number, staticValues: Calc.StaticValue[]): Calc.StaticValue => {
    return staticValues
      // we want to return the highest value, that is less than the BMI.
      .reduce((selected, current) => {
        const currVal = parseFloat(current.value);
        const selVal = parseFloat(selected.value);

        if (currVal > bmi) return selected;

        if (currVal <= selVal) return selected;

        return current;
      }, { value: -1 } as unknown as Calc.StaticValue);
  };

  function ResultBody({ results }: { results: typeof bmi }) {
    const formula = listify(props.formula)[0];
    const staticInput = listify(props.staticInputs)[0];

    return results.value > 0 && (
      <div className="l-mt-2">
        <ThemeBox size="large" theme="blue-light">
          <div className="results">
            <h2 className="">
              {formula.displayName}
              :
              {" "}
              {results.value}
            </h2>

            <div className="t-body-text">
              <Richtext className="t-body-text" text={results.data.text} />

              <Richtext className="t-body-text" text={staticInput.tekst} />
            </div>
          </div>
        </ThemeBox>
      </div>
    );
  }

  return (
    <div className="bmi-calc">
      <div className="b-article-intro__text l-mt-2">
        {props.data.tittel && <h1>{props.data.tittel}</h1>}
        {props.data.intro && <div className="b-article-intro__intro">{props.data.intro}</div>}
        {props.data.tekst && <Richtext className="t-body-text" text={props.data.tekst} />}
      </div>
      <form className="form l-mt-2 l-mb-2">
        <div className="input-container">
          {props.inputs.map((el) => (
            <TextField
              onChange={getInputHandler(el.techName)}
              key={el.techName}
              fullWidth
              className="input-field"
              type="number"
              min={0}
              placeholder="0"
              value={values[el.techName]}
              label={el.displayName}
              name={el.techName}
            />
          ))}
        </div>

        <Button type="submit" onClick={(e) => updateBmi(e)}>Beregn KMI</Button>
      </form>

      <ResultBody results={bmi} />

      {props.data.textUnderCalculator && <Richtext className="t-body-text" text={props.data.textUnderCalculator} />}

    </div>
  );
}
export default function (props) {
  return <Part {...props} />;
}
